import { RouterReducerState } from '@ngrx/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RouterStateUrl } from '@sd-portal/shared/util';
import { State } from '..';
import * as fromAircraftProfile from './aircraft-profile.reducer';

// feature selectors
const getSharedAuditState = createFeatureSelector<State>('sharedAudit');
const getRouterState = createFeatureSelector<RouterReducerState<RouterStateUrl>>('router');

// feature slice - aircraft profile
const getAircraftProfileSubState = createSelector(
  getSharedAuditState,
  state => state.aircraftProfile
);

// aircraft profile slice selectors
const getAircraftProfileSkeletonState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftProfileSkeleton
);
const getAircraftProfileAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftProfileAudit
);
const getAircraftPerformanceAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftPerformanceAudit
);
const getAircraftProfileEngineAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftProfileEngineAudit
);
const getEngineAuditState = createSelector(getAircraftProfileSubState, state => state.engineAudit);
const getAircraftProfileComponentAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftProfileComponentAudit
);
const getComponentAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.componentAudit
);
const getColorSettingAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.colorSettingAudit
);
const getPostFlightVisibilityAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.postFlightVisibilityAudit
);
const getAircraftBillingRateAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftBillingRateAudit
);
const getAircraftRelationAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftRelationAudit
);
const getPhoneNumberAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.phoneNumberAudit
);
const getFaxNumberAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.faxNumberAudit
);
const getEmailAddressAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.emailAddressAudit
);
const getAddressAuditState = createSelector(
  getAircraftProfileSubState,
  state => state.addressAudit
);
// reference slices
const getAircraftRelationTypeState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftRelationType
);
const getAircraftRelationSubTypeState = createSelector(
  getAircraftProfileSubState,
  state => state.aircraftRelationSubType
);
const getPostFlightVisibilityTypeState = createSelector(
  getAircraftProfileSubState,
  state => state.postFlightVisibilityType
);

// router param selector
export const getAircraftProfileId = createSelector(
  getRouterState,
  state => +state.state.params['id']
);

// aircraftProfileSkeleton selectors
export const getAircraftProfileSkeletonLoaded = createSelector(
  getAircraftProfileSkeletonState,
  state => state.loaded
);
export const getAircraftProfileSkeletonLoading = createSelector(
  getAircraftProfileSkeletonState,
  state => state.loading
);
// const getAircraftProfileSkeletonError = createSelector(
//   getAircraftProfileSkeletonState,
//   state => state.error
// );
// const getAircraftProfileAuditSkeletons = createSelector(
//   getAircraftProfileSkeletonState,
//   getAircraftProfileSkeletonLoaded,
//   (state, isLoaded) => {
//     return isLoaded ? state.entities : null;
//   }
// );
export const getAircraftProfileAuditSkeleton = createSelector(
  getAircraftProfileSkeletonState,
  getAircraftProfileSkeletonLoaded,
  getAircraftProfileId,
  (state, isLoaded, aircraftProfileId) => {
    return isLoaded ? state.entities[aircraftProfileId] : null;
  }
);

// aircraftProfileAudit selectors
export const getAircraftProfileAuditLoaded = createSelector(
  getAircraftProfileAuditState,
  state => state.loaded
);
export const getAircraftProfileAuditLoading = createSelector(
  getAircraftProfileAuditState,
  state => state.loading
);
export const getAircraftProfileAudits = createSelector(
  getAircraftProfileAuditState,
  fromAircraftProfile.aircraftProfileAuditSelectors.selectAll
);

// aircraftPerformance selectors
export const getAircraftPerformanceAuditLoaded = createSelector(
  getAircraftPerformanceAuditState,
  state => state.loaded
);
export const getAircraftPerformanceAuditLoading = createSelector(
  getAircraftPerformanceAuditState,
  state => state.loading
);
export const getAircraftPerformanceAudits = createSelector(
  getAircraftPerformanceAuditState,
  fromAircraftProfile.aircraftPerformanceAuditSelectors.selectAll
);

// aircraftProfileEngine selectors
export const getAircraftProfileEngineAuditLoaded = createSelector(
  getAircraftProfileEngineAuditState,
  state => state.loaded
);
export const getAircraftProfileEngineAuditLoading = createSelector(
  getAircraftProfileEngineAuditState,
  state => state.loading
);
export const getAircraftProfileEngineAudits = createSelector(
  getAircraftProfileEngineAuditState,
  fromAircraftProfile.aircraftProfileEngineAuditSelectors.selectAll
);

// engine selectors
export const getEngineAuditLoaded = createSelector(getEngineAuditState, state => state.loaded);
export const getEngineAuditLoading = createSelector(getEngineAuditState, state => state.loading);
export const getEngineAudits = createSelector(
  getEngineAuditState,
  fromAircraftProfile.engineAuditSelectors.selectAll
);

// aircraftProfileComponent selectors
export const getAircraftProfileComponentAuditLoaded = createSelector(
  getAircraftProfileComponentAuditState,
  state => state.loaded
);
export const getAircraftProfileComponentAuditLoading = createSelector(
  getAircraftProfileComponentAuditState,
  state => state.loading
);
export const getAircraftProfileComponentAudits = createSelector(
  getAircraftProfileComponentAuditState,
  fromAircraftProfile.aircraftProfileComponentAuditSelectors.selectAll
);

// Component selectors
export const getComponentAuditLoaded = createSelector(
  getComponentAuditState,
  state => state.loaded
);
export const getComponentAuditLoading = createSelector(
  getComponentAuditState,
  state => state.loading
);
export const getComponentAudits = createSelector(
  getComponentAuditState,
  fromAircraftProfile.componentAuditSelectors.selectAll
);

// colorSetting selectors
export const getColorSettingAuditLoaded = createSelector(
  getColorSettingAuditState,
  state => state.loaded
);
export const getColorSettingAuditLoading = createSelector(
  getColorSettingAuditState,
  state => state.loading
);
export const getColorSettingAudits = createSelector(
  getColorSettingAuditState,
  fromAircraftProfile.colorSettingAuditSelectors.selectAll
);

// postFlightVisibility selectors
export const getPostFlightVisibilityAuditLoaded = createSelector(
  getPostFlightVisibilityAuditState,
  state => state.loaded
);
export const getPostFlightVisibilityAuditLoading = createSelector(
  getPostFlightVisibilityAuditState,
  state => state.loading
);
export const getPostFlightVisibilityAudits = createSelector(
  getPostFlightVisibilityAuditState,
  fromAircraftProfile.postFlightVisibilityAuditSelectors.selectAll
);

// aircraftBillingRate selectors
export const getAircraftBillingRateAuditLoaded = createSelector(
  getAircraftBillingRateAuditState,
  state => state.loaded
);
export const getAircraftBillingRateAuditLoading = createSelector(
  getAircraftBillingRateAuditState,
  state => state.loading
);
export const getAircraftBillingRateAudits = createSelector(
  getAircraftBillingRateAuditState,
  fromAircraftProfile.aircraftBillingRateAuditSelectors.selectAll
);

// aircraftRelation selectors
export const getAircraftRelationAuditLoaded = createSelector(
  getAircraftRelationAuditState,
  state => state.loaded
);
export const getAircraftRelationAuditLoading = createSelector(
  getAircraftRelationAuditState,
  state => state.loading
);
export const getAircraftRelationAudits = createSelector(
  getAircraftRelationAuditState,
  fromAircraftProfile.aircraftRelationAuditSelectors.selectAll
);

// phoneNumber selectors
export const getPhoneNumberAuditLoaded = createSelector(
  getPhoneNumberAuditState,
  state => state.loaded
);
export const getPhoneNumberAuditLoading = createSelector(
  getPhoneNumberAuditState,
  state => state.loading
);
export const getPhoneNumberAudits = createSelector(
  getPhoneNumberAuditState,
  fromAircraftProfile.phoneNumberAuditSelectors.selectAll
);

// faxNumber selectors
export const getFaxNumberAuditLoaded = createSelector(
  getFaxNumberAuditState,
  state => state.loaded
);
export const getFaxNumberAuditLoading = createSelector(
  getFaxNumberAuditState,
  state => state.loading
);
export const getFaxNumberAudits = createSelector(
  getFaxNumberAuditState,
  fromAircraftProfile.faxNumberAuditSelectors.selectAll
);

// emailAddress selectors
export const getEmailAddressAuditLoaded = createSelector(
  getEmailAddressAuditState,
  state => state.loaded
);
export const getEmailAddressAuditLoading = createSelector(
  getEmailAddressAuditState,
  state => state.loading
);
export const getEmailAddressAudits = createSelector(
  getEmailAddressAuditState,
  fromAircraftProfile.emailAddressAuditSelectors.selectAll
);

// address selectors
export const getAddressAuditLoaded = createSelector(getAddressAuditState, state => state.loaded);
export const getAddressAuditLoading = createSelector(getAddressAuditState, state => state.loading);
export const getAddressAudits = createSelector(
  getAddressAuditState,
  fromAircraftProfile.addressAuditSelectors.selectAll
);

// aircraftRelationType selectors
export const getAircraftRelationTypesLoaded = createSelector(
  getAircraftRelationTypeState,
  state => state.loaded
);
export const getAircraftRelationTypesLoading = createSelector(
  getAircraftRelationTypeState,
  state => state.loading
);
export const getAircraftRelationTypes = createSelector(
  getAircraftRelationTypeState,
  fromAircraftProfile.aircraftRelationTypeSelectors.selectAll
);

// aircraftRelationSubType selectors
export const getAircraftRelationSubTypeLoaded = createSelector(
  getAircraftRelationSubTypeState,
  state => state.loaded
);
export const getAircraftRelationSubTypeLoading = createSelector(
  getAircraftRelationSubTypeState,
  state => state.loading
);
export const getAircraftRelationSubTypes = createSelector(
  getAircraftRelationSubTypeState,
  fromAircraftProfile.aircraftRelationSubTypeSelectors.selectAll
);

// postFlightVisibilityType selectors
export const getPostFlightVisibilityTypesLoaded = createSelector(
  getPostFlightVisibilityTypeState,
  state => state.loaded
);
export const getPostFlightVisibilityTypesLoading = createSelector(
  getPostFlightVisibilityTypeState,
  state => state.loading
);
export const getPostFlightVisibilityTypes = createSelector(
  getPostFlightVisibilityTypeState,
  fromAircraftProfile.postFlightVisibilityTypeSelectors.selectAll
);
